// TSMED-1482: core list endpoints intermittently return `{ data: [] }` for a
// brief, self-healing window (1-3 poll cycles). Rendering that empty array
// blanks the board. This reducer keeps the last non-empty payload through empty
// polls and flags staleness only once the empty streak is sustained. It is pure
// (no React) so the policy can be unit-tested in isolation from the hook that
// owns the state.
//
// Premise: an empty list means "backend unavailable / stale", never a legitimate
// state. ARCH-005 §9 asserts this for activeCases / surgeons — an assumption about
// NOP, not an enforced invariant; the net is applied to aircraftStatuses /
// hubsResourceReport on the same assumption.
//
// TMDXNC-238: newCases was removed from the net — the premise is false for it.
// NOP routinely has zero new cases, so holding the last non-empty payload kept
// closed cases on the board indefinitely (there is no expiry here: `lastGood` is
// served for as long as the empties continue). Before adding a caller, confirm
// the premise actually holds for that endpoint.

// One poll payload: the list plus the timestamp the backend generated it.
// `TItem` is the list ELEMENT type (contrast DataWithValidation<T> in this
// folder, where `T` is the whole payload).
export interface PollPayload<TItem> {
  data: TItem[];
  generatedAt: string | null;
}

export interface StaleState<TItem> {
  lastGood: PollPayload<TItem> | null;
  consecutiveEmpty: number;
}

export interface StaleOutput<TItem> extends PollPayload<TItem> {
  isStale: boolean;
}

export const initialStaleState = <TItem>(): StaleState<TItem> => ({
  lastGood: null,
  consecutiveEmpty: 0,
});

export const applyStaleWhileEmpty = <TItem>(
  state: StaleState<TItem>,
  incoming: PollPayload<TItem>,
  threshold: number,
): { state: StaleState<TItem>; output: StaleOutput<TItem> } => {
  if (incoming.data.length > 0) {
    const lastGood: PollPayload<TItem> = {
      data: incoming.data,
      generatedAt: incoming.generatedAt,
    };
    return {
      state: { lastGood, consecutiveEmpty: 0 },
      output: { ...lastGood, isStale: false },
    };
  }

  const consecutiveEmpty = state.consecutiveEmpty + 1;

  if (!state.lastGood) {
    // No good payload to fall back to yet — pass the empty response through
    // unchanged, as if this layer were absent.
    return {
      state: { lastGood: null, consecutiveEmpty },
      output: {
        data: incoming.data,
        generatedAt: incoming.generatedAt,
        isStale: false,
      },
    };
  }

  return {
    state: { lastGood: state.lastGood, consecutiveEmpty },
    output: {
      data: state.lastGood.data,
      generatedAt: state.lastGood.generatedAt,
      isStale: consecutiveEmpty >= threshold,
    },
  };
};
